import { core, type Day } from '@map-my-money/engine';

/** Approximate rendered width of Inter text; good enough to lay out labels without measuring the DOM. */
export function textWidth(text: string, fontSize: number, weight = 600): number {
  let units = 0;
  for (const ch of text) {
    if (/[0-9]/.test(ch)) units += 0.6;
    else if (/[£$€]/.test(ch)) units += 0.6;
    else if (/[,.:·'’ ]/.test(ch)) units += 0.3;
    else if (/[A-Z]/.test(ch)) units += 0.68;
    else if (/[mwMW]/.test(ch)) units += 0.85;
    else if (/[il|]/.test(ch)) units += 0.26;
    else units += 0.55;
  }
  return units * fontSize * (weight >= 700 ? 1.05 : 1);
}

/** Evenly spaced year ticks: every n years from the start, labelled with the calendar year. */
export function yearTicks(start: Day, end: Day, maxTicks: number): Day[] {
  return relativeYearTicks(start, end, maxTicks);
}

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const overlaps = (a: Box, b: Box, pad = 4) =>
  a.x < b.x + b.w + pad && a.x + a.w + pad > b.x && a.y < b.y + b.h + pad && a.y + a.h + pad > b.y;

export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Index of the item whose x is nearest to `x` (items sorted by x). */
export function nearestIndex(xs: readonly number[], x: number): number {
  let lo = 0;
  let hi = xs.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (xs[mid]! < x) lo = mid;
    else hi = mid;
  }
  return Math.abs(xs[lo]! - x) <= Math.abs(xs[hi]! - x) ? lo : hi;
}

/** Nice money ticks for an axis: 4–5 values including 0 when the domain spans it. */
export function niceStep(range: number, targetTicks: number): number {
  const raw = range / Math.max(1, targetTicks);
  const magnitude = 10 ** Math.floor(Math.log10(raw || 1));
  const residual = raw / magnitude;
  const nice = residual > 5 ? 10 : residual > 2.5 ? 5 : residual > 2 ? 2.5 : residual > 1 ? 2 : 1;
  return nice * magnitude;
}

export function moneyTicks(min: number, max: number, target = 4, tight = false): { ticks: number[]; lo: number; hi: number } {
  const step = niceStep(max - min, target);
  // A shallow dip below zero (a little debt) gets headroom, not a whole extra gridline; a tight
  // (zoomed) axis starts at the data, not at the tick below it.
  const lo = tight ? min : min < 0 && -min < step * 0.3 ? min * 1.25 : Math.floor(min / step) * step;
  // Headroom above the data rather than up to the next round tick, so the plot fills its box.
  const hi = Math.max(max + (max - Math.min(0, lo)) * 0.06, step);
  const ticks: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) ticks.push(Math.round(v));
  return { ticks, lo, hi };
}

/** Ticks every whole number of years from `start` (Y0, Y1, …), thinned to fit. */
export function relativeYearTicks(start: Day, end: Day, maxTicks: number): Day[] {
  const years = Math.round((end - start) / 365.25);
  const step = [1, 2, 5, 10, 25].find((s) => Math.floor(years / s) + 1 <= maxTicks) ?? 25;
  const ticks: Day[] = [];
  for (let y = 0; y <= years; y += step) ticks.push(core.addYears(start, y));
  return ticks;
}
